import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Linking, Platform, Pressable, TextInput, View, Text, StyleSheet } from 'react-native';
import type { StorageInterface } from '../../../stores/childNavStores';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import { SkinTypeDefStorage } from '../../../storage/SkinTypeDefStorage';
import type {
  AccountPlaDto,
  AccountPracInfoResponseDto,
  AccountSettingsDto,
  AccountSettingsUpdateDto,
  AdminPreLoadDto,
  AdminUVarDto,
  PracAccountSettingUpdateDto,
  PracSraUpdateDto,
  RegistyDto,
  SettingDto,
  SkinAssessmentListDto,
  SkinTypeDefDto,
  TouchPointScheduleDto,
} from '../../../types/api';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import { OrderedDualListSelector, type OrderedOption } from './OrderedDualListSelector';
import { getManagedAccountSearchResults, type AccountSearchResult } from './ManageAccountsView';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';

interface AccountSettingsViewProps {
  accountData?: AccountPracInfoResponseDto | null;
  isLoading?: boolean;
  error?: string | null;
  storage?: StorageInterface | null;
  accountId?: string | null;
  apiService?: any;
  /**
   * The active account's SkinTypeDef — resolved by the parent (see useAccountSkinDef) from the
   * bundled content already synced locally, replacing the AccountSettingsDto.SkinDef field that
   * used to require a per-sign-in backend fetch of the same data. null while loading/unavailable.
   */
  skinDef?: SkinTypeDefDto | null;
  /** False until a real skinDef lookup has finished — see useAccountSkinDef's doc comment for why
   * the hydration effect below must wait for this rather than treating null as final. */
  skinDefReady?: boolean;
}

const SECTION_LABELS = ['Settings', 'Account Info', 'Website Links'] as const;

const ACCOUNT_SETTINGS_SPACING = {
  topPanelPadding: 14,
  topActionsBottom: 6,
  saveButtonTop: 2,
  secondColumnContentTop: 2,
  secondColumnContentBottom: 6,
  secondColumnRowGap: 6,
  accountInfoRowVerticalPadding: 2,
  accountInfoRowMinHeight: 25,
  websiteCardVerticalPadding: 4,
  websiteUrlTopPadding: 6,
  editFormToSettingsGap: 20,
  sectionSpacerRow: 12,
} as const;

// Hardcoded — PracInfoDto.AccessType only distinguishes Pro vs Groups practice accounts,
// unrelated to the SRA sign-in accessType (10/20/25/30) used elsewhere in this file.
const ACCOUNT_TYPE_OPTIONS = [
  { value: '10', label: 'Pro' },
  { value: '11', label: 'Groups' },
] as const;

const MANAGED_ENABLES = [
  { key: 'enable_idmgmt', label: 'Enable client ID numbers' },
  { key: 'enable_dobmgmt', label: 'Enable client DOB' },
  { key: 'enable_notifycomplete', label: 'Enable assessment completed notifications' },
  { key: 'enable_phimgmt', label: 'Enable client information management' },
  { key: 'enable_schedulemgmt', label: 'Enable schedule management' },
  { key: 'enable_patconnmgmt', label: 'Enable connection management' },
  { key: 'enable_patrefmgmt', label: 'Enable flag and referral management' },
  { key: 'enable_episodemgmt', label: 'Enable episode management' },
  { key: 'enable_medinfomgmt', label: 'Enable extended information management' },
  { key: 'enable_messagedirect', label: 'Enable client messaging' },
] as const;

type AccountSettingsDraft = {
  sourceSignature?: string;
  // '0' = disabled, '1' = enabled, '-1' = hidden (see AccountSettingsView's 3-state Settings rows)
  enableState: Record<string, string>;
  websiteEnableState: Record<string, boolean>;
  accountName: string;
  returnWebsite: string;
  settingEditState?: Record<string, SettingEditDraft>;
};

type SettingEditDraft = {
  alias: string;
  notes: string;
  selectedOptionIds?: string[];
};

const normalizeOptionId = (value: string): string => {
  return value.trim().toLowerCase();
};

const getPhiOptionId = (item: { MVarId?: string; Name?: string; DName?: string }): string => {
  return normalizeOptionId(item.MVarId || item.Name || item.DName || '');
};

const getScheduleOptionId = (schedule: TouchPointScheduleDto): string => {
  return normalizeOptionId(schedule.SchedId || schedule.DName || '');
};

const getRegistryOptionId = (registry: RegistyDto): string => {
  return normalizeOptionId(registry.RegistryId || '');
};

// Nested enables shown inside the "Enable schedule management" edit panel rather than as
// their own top-level Settings rows — still persisted via the same enableState/Enables flow.
const SCHEDULE_MGMT_SUB_ENABLES = ['enable_manualscheduling', 'enable_touchpointscheduling'] as const;

// Edit-panel header overrides — the Settings row checkbox keeps its "Enable ..." label,
// but its edit panel (opened via that row's "Edit" button) describes the action being taken there.
const SETTING_EDIT_PANEL_TITLES: Record<string, string> = {
  enable_phimgmt: 'Select client information to manage',
  enable_schedulemgmt: 'Select active touchpoint schedules',
};

const parsePos = (value: string): number => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const isSettingAdditionalEditable = (settingKey: string): boolean => {
  return settingKey !== 'enable_idmgmt'
    && settingKey !== 'enable_dobmgmt'
    && settingKey !== 'enable_notifycomplete';
};

const isTruthyValue = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'active' || normalized === 'enabled';
};

const normalizeUrl = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const getSettingValue = (settings: Array<{ Name: string; Value: string }> | undefined, settingName: string): string | null => {
  if (!settings || settings.length === 0) {
    return null;
  }

  const target = settingName.trim().toLowerCase();
  return settings.find((item) => item.Name.trim().toLowerCase() === target)?.Value ?? null;
};

const getLaunchDisplayName = (entry: NonNullable<AccountPracInfoResponseDto['AccountSettings']>['PlaList'][number]): string => {
  return getSettingValue(entry.LaunchInfo, 'launchqueue_survey_name')
    || entry.DName
    || entry.SCode
    || 'Website link';
};

const getWebsiteLinkUrl = (entry: NonNullable<AccountPracInfoResponseDto['AccountSettings']>['PlaList'][number]): string => {
  const remoteBaseUrl = getSettingValue(entry.RemoteInfo, 'baseurl')
    || getSettingValue(entry.RemoteInfo, 'base_url')
    || getSettingValue(entry.RemoteInfo, 'returnurl')
    || getSettingValue(entry.RemoteInfo, 'return_url')
    || getSettingValue(entry.RemoteInfo, 'website')
    || getSettingValue(entry.RemoteInfo, 'url');

  const normalized = normalizeUrl(remoteBaseUrl);
  if (normalized) {
    return `${normalized}/link/${entry.SCode}`;
  }

  return `${resolveBaseUrl(null)}/link/${entry.SCode}`;
};

const getWebsiteLinkEnabled = (entry: NonNullable<AccountPracInfoResponseDto['AccountSettings']>['PlaList'][number]): boolean => {
  if (isTruthyValue(entry.Active) && !isTruthyValue(entry.Suspended)) {
    return true;
  }

  return entry.RemoteInfo.some((item) => {
    const name = item.Name.trim().toLowerCase();
    return (name.includes('active') || name.includes('enabled')) && isTruthyValue(item.Value);
  });
};

const resolveBaseUrl = (website: string | null | undefined): string => {
  const normalizedWebsite = normalizeUrl(website);
  if (normalizedWebsite) {
    try {
      return new URL(normalizedWebsite).origin;
    } catch {
      // fall through to global location fallback
    }
  }

  const globalLocation = (globalThis as any)?.location?.origin;
  if (typeof globalLocation === 'string' && globalLocation.length > 0) {
    return globalLocation;
  }

  return 'https://example.com';
};

const webDropdownStyle = { ...webControlStyle, height: 28 } as any;

const renderCheckbox = (checked: boolean) => {
  return (
    <View style={[styles.checkboxBtn, checked && styles.checkboxBtnChecked]}>
      {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
    </View>
  );
};

const renderWebCheckbox = (
  checked: boolean,
  onToggle: (nextChecked: boolean) => void,
  accessibilityLabel: string
) => {
  return React.createElement(
    'input' as any,
    {
      type: 'checkbox',
      'aria-label': accessibilityLabel,
      checked,
      onChange: (event: any) => onToggle(Boolean(event?.target?.checked)),
      style: {
        width: '22px',
        height: '22px',
        accentColor: EDITOR_COLORS.accent,
        cursor: 'pointer',
      } as const,
    }
  );
};

const getAccountSettingsDraftKey = (accountId: string): string => {
  return `account_settings_draft_${accountId}`;
};

const getAccountSettingsSignature = (data?: AccountPracInfoResponseDto | null): string => {
  const settings = data?.AccountSettings;
  if (!settings) {
    return 'none';
  }

  // Must be sensitive to VALUE changes on existing entries, not just counts — a Setting
  // flipped to hidden (-1) out-of-band (e.g. directly at the backend) keeps the same
  // Enables.length, so a count-only signature would never notice the change and this
  // component would keep serving its stale in-memory state indefinitely.
  const enablesSignature = (settings.Enables ?? [])
    .map((item) => `${item.Name.trim().toLowerCase()}:${item.Value}`)
    .sort()
    .join(',');
  const plaCount = settings.PlaList?.length ?? 0;
  const sraEmail = data?.SraUser?.Email ?? '';
  const pracInfo = settings.PracInfo;
  const pracSignature = [
    pracInfo?.DName ?? '',
    pracInfo?.Website ?? '',
    pracInfo?.Enabled ?? '',
    pracInfo?.IsParent ?? '',
    pracInfo?.Parent ?? '',
    pracInfo?.SkinType ?? '',
    pracInfo?.AccessType ?? '',
  ].join(':');
  // Registries (schedule management's selected touchpoint schedules) and PracPhi (client
  // info management's selected fields) drive their own edit panels' dual-list selection —
  // same "must be sensitive to VALUE changes" reasoning as Enables above applies to them:
  // without this, a draft captured while these were empty/unwired stays "fresh" forever
  // (nothing else about the signature changes), so its stale, still-empty selectedOptionIds
  // silently wins over the freshly computed selection every time this hydrates.
  const registriesSignature = (settings.Registries ?? [])
    .map((item) => `${item.RegistryId.trim().toLowerCase()}:${item.Pos}`)
    .sort()
    .join(',');
  const pracPhiSignature = (settings.PracPhi ?? [])
    .map((item) => `${(item.MVarId || item.Name).trim().toLowerCase()}:${item.Pos}`)
    .sort()
    .join(',');
  // AccountSettings.Registries (above) routinely arrives BEFORE Touchpoints.Schedules does
  // (the former is part of the first, AccountSettings-only fetch; the latter only shows up
  // once the background prefetch resolves). scheduleOptions — and therefore
  // initialScheduleSelectedIds, which needs both Registries AND scheduleOptions to match
  // against — can still be computed as empty on that first pass even though Registries
  // itself is already correct and stable. Since this signature was otherwise built entirely
  // from AccountSettings, it stayed unchanged when Touchpoints.Schedules showed up moments
  // later, so the draft captured on that first (scheduleOptions-empty) pass looked "fresh"
  // forever and its stale, empty schedule selection permanently won over the correct one.
  // Folding Touchpoints.Schedules in here makes its arrival register as a real change.
  const touchpointsSchedulesSignature = (data?.Touchpoints?.Schedules ?? [])
    .map((item) => (item.SchedId || '').trim().toLowerCase())
    .sort()
    .join(',');
  return `${enablesSignature}|${plaCount}|${sraEmail}|${pracSignature}|${registriesSignature}|${pracPhiSignature}|${touchpointsSchedulesSignature}`;
};

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

const normalizeEnableValue = (value: string | null | undefined): string => {
  return value === '1' || value === '-1' ? value : '0';
};

const updateEnableSettings = (
  existing: SettingDto[] | undefined,
  enableState: Record<string, string>
): SettingDto[] => {
  const rows = existing ?? [];
  const seen = new Set<string>();

  const updated = rows.map((entry) => {
    const key = entry.Name.trim().toLowerCase();
    seen.add(key);

    if (!Object.prototype.hasOwnProperty.call(enableState, key)) {
      return entry;
    }

    return {
      ...entry,
      Value: normalizeEnableValue(enableState[key]),
    };
  });

  Object.keys(enableState).forEach((key) => {
    if (seen.has(key)) {
      return;
    }

    updated.push({
      Name: key,
      Value: normalizeEnableValue(enableState[key]),
    });
  });

  return updated;
};

const updatePlaListEnableState = (
  list: AccountPlaDto[] | undefined,
  websiteEnableState: Record<string, boolean>
): AccountPlaDto[] => {
  return (list ?? []).map((entry) => {
    const key = `${entry.PlaId}:${entry.SCode}`;
    if (!Object.prototype.hasOwnProperty.call(websiteEnableState, key)) {
      return entry;
    }

    const enabled = !!websiteEnableState[key];
    const nextValue = enabled ? '1' : '0';
    const nextRemoteInfo = (entry.RemoteInfo ?? []).map((item) => {
      const name = item.Name.trim().toLowerCase();
      if (name.includes('active') || name.includes('enabled')) {
        return {
          ...item,
          Value: nextValue,
        };
      }

      return item;
    });

    return {
      ...entry,
      Active: nextValue,
      Suspended: enabled ? '0' : (entry.Suspended || '0'),
      RemoteInfo: nextRemoteInfo,
    };
  });
};

/**
 * Map AdminAssessmentDto to AccountPlaDto fields
 * Preloads and Reports will be configured later via Administration Options tab
 * Returns partial Pla with basic assessment linkage
 */
const mapAssessmentToPla = async (
  assessmentId: string,
  storage: StorageInterface | null | undefined
): Promise<Partial<AccountPlaDto>> => {
  const result: Partial<AccountPlaDto> = {
    PlaId: '',
    SCode: '',
    AccountId: '',
    AssessmentId: assessmentId,
    DName: '',
    Desc: '',
    LaunchInfo: [],
    ReturnUrl: '',
    ProtoPlaId: '',
    Active: '1',
    Suspended: '0',
    Status: '2',
    Pos: '0',
    RemoteInfo: [],
    Preloads: [],
    Reports: [],
  };

  if (!storage) {
    console.warn('⚠️ mapAssessmentToPla: No storage available, returning stub Pla');
    return result;
  }

  try {
    // Fetch assessment data
    const assessmentStorage = new AdminAssessmentStorage(storage);
    const assessment: AdminAssessmentDto | null = await assessmentStorage.getAssessmentBySourceId(assessmentId);

    if (!assessment) {
      console.warn(`⚠️ mapAssessmentToPla: Assessment not found for ID ${assessmentId}, returning stub Pla`);
      return result;
    }

    // Map basic fields
    result.DName = assessment.dName || assessmentId;

    // Build LaunchInfo settings
    result.LaunchInfo = [
      { Name: 'SurveyId', Value: assessment.surveyId || '' },
      { Name: 'SurveybList', Value: assessment.surveybList || '' },
      { Name: 'LanguageTypeId', Value: assessment.languageTypeId || '' },
      { Name: 'Extra', Value: assessment.extra || '' },
    ];

    // Note: Preloads and Reports are left empty - will be configured via Administration Options tab

    console.log(`✅ mapAssessmentToPla: Mapped assessment ${assessmentId} (DName: ${result.DName})`);

    return result;
  } catch (error) {
    console.error(`❌ mapAssessmentToPla: Error mapping assessment ${assessmentId}:`, error);
    return result;
  }
};

const updatePlaListWithAssessments = async (
  list: AccountPlaDto[] | undefined,
  selectedAssessmentIds: string[],
  storage: StorageInterface | null | undefined
): Promise<AccountPlaDto[]> => {
  const existing = list ?? [];
  const existingByAssessmentId = new Map<string, AccountPlaDto>();
  const existingWithoutAssessments: AccountPlaDto[] = [];

  // Separate existing PlaList into assessment-linked and non-assessment entries
  existing.forEach((entry) => {
    if (entry.AssessmentId && entry.AssessmentId.trim()) {
      existingByAssessmentId.set(entry.AssessmentId, entry);
    } else {
      existingWithoutAssessments.push(entry);
    }
  });

  // Build the new assessment-linked entries with full data hydration and set Pos based on order
  const assessmentEntries = await Promise.all(
    selectedAssessmentIds.map(async (assessmentId, index) => {
      const existingEntry = existingByAssessmentId.get(assessmentId);
      if (existingEntry) {
        // Keep existing entry but update Pos based on order in selectedAssessmentIds
        return {
          ...existingEntry,
          Pos: String(index + 1),
        };
      }

      // Create new entry with full assessment data and set Pos
      const mappedPla = await mapAssessmentToPla(assessmentId, storage);
      return {
        ...mappedPla,
        Pos: String(index + 1),
      } as AccountPlaDto;
    })
  );

  // Return non-assessment entries + selected assessment entries
  return [...existingWithoutAssessments, ...assessmentEntries];
};

// ── Pro (accessType 10) assessment settings ────────────────────────────────────────────────
// Pro accounts no longer see the Assessment Options tab, so the two things they need from it
// live here in Settings instead: the "client report after administration" switch (Assessment
// Options' per-assessment Admin Report, i.e. the 'adminreportid' preload) and each configured
// assessment's skin-defined preloads. Both are stored on the PLA's Preloads, exactly where
// Assessment Options wrote them.

const ADMIN_REPORT_PRELOAD = 'adminreportid';
const DEFAULT_REPORT_PRELOAD = 'defaultreportid';

/** Pro-mode draft: the client-report switch plus per-PLA (key = `${PlaId}:${SCode}`) preload values. */
type ProPlaState = {
  clientReportEnabled: boolean;
  preloadValues: Record<string, Record<string, string>>;
};

const getPlaKey = (pla: AccountPlaDto): string => `${pla.PlaId}:${pla.SCode}`;

const getPlaPreloadValue = (pla: AccountPlaDto, fieldCode: string): string => {
  return (pla.Preloads ?? []).find((preload) => preload.FieldCode === fieldCode)?.Value ?? '';
};

// pla.Reports round-trips as raw JSON, so a saved report can carry PascalCase ReportId even
// though the type says camelCase (same seam AssessmentOptionsView.getReportId reads around).
const getReportIdOf = (report: any): string => report?.reportId || report?.ReportId || '';

/**
 * Report ids enabled for a configured assessment — what Assessment Options' Admin Report
 * dropdown lists: the PLA's saved Reports subset when it has one, otherwise every report the
 * assessment defines.
 */
const getAvailableReportIds = (pla: AccountPlaDto, assessment: AdminAssessmentDto | undefined): string[] => {
  const source: any[] = pla.Reports != null ? pla.Reports : (assessment?.reports ?? []);
  return source.map(getReportIdOf).filter((id): id is string => !!id);
};

const upsertPreload = (list: AdminPreLoadDto[], preload: AdminPreLoadDto): AdminPreLoadDto[] => {
  const index = list.findIndex((item) => item.FieldCode === preload.FieldCode);
  if (index < 0) {
    return [...list, preload];
  }
  const next = [...list];
  next[index] = preload;
  return next;
};

/**
 * Applies the Pro-mode draft to the PlaList being saved: writes each configured assessment's
 * skin-defined preload values and turns the client report (the 'adminreportid' preload) on/off.
 * Enabling keeps an already-chosen report, else uses the assessment's default report, else its
 * first available one — Pro can't pick per assessment since Assessment Options is hidden.
 * Every touched PLA also carries the account's current UVars, matching what Assessment Options
 * stamps on save (see AssessmentOptionsView.handleSave) so uvar-restricted answers still reach
 * /link, /remote and /secure sessions.
 */
const applyProPlaState = (
  list: AccountPlaDto[],
  state: ProPlaState,
  skinAssessments: SkinAssessmentListDto[],
  assessmentsById: Record<string, AdminAssessmentDto>,
  accountUVars: AdminUVarDto[]
): AccountPlaDto[] => {
  return list.map((pla) => {
    const assessmentId = pla.AssessmentId?.trim();
    if (!assessmentId) {
      return pla;
    }
    const key = getPlaKey(pla);
    const values = state.preloadValues[key];
    if (!values) {
      return pla;
    }

    let preloads: AdminPreLoadDto[] = [...(pla.Preloads ?? [])];

    const skinAssessment = skinAssessments.find((item) => item.AssessmentId === assessmentId);
    (skinAssessment?.PreloadOverrides ?? []).forEach((override) => {
      const value = values[override.FieldCode];
      if (value === undefined) {
        return;
      }
      preloads = upsertPreload(preloads, {
        FieldCode: override.FieldCode,
        Type: override.Type,
        Value: value,
        LanguageTypeId: override.LanguageTypeId || '1',
      });
    });

    const currentAdminReport = getPlaPreloadValue(pla, ADMIN_REPORT_PRELOAD);
    const availableIds = getAvailableReportIds(pla, assessmentsById[assessmentId]);
    const defaultReport = getPlaPreloadValue(pla, DEFAULT_REPORT_PRELOAD);
    const adminReportValue = state.clientReportEnabled
      ? (currentAdminReport || (availableIds.includes(defaultReport) ? defaultReport : availableIds[0]) || '')
      : '';
    // Don't create an empty adminreportid on a PLA that never had one.
    if (adminReportValue || preloads.some((item) => item.FieldCode === ADMIN_REPORT_PRELOAD)) {
      preloads = upsertPreload(preloads, {
        FieldCode: ADMIN_REPORT_PRELOAD,
        Type: 'string',
        Value: adminReportValue,
        LanguageTypeId: '1',
      });
    }

    return { ...pla, Preloads: preloads, UVars: accountUVars };
  });
};

const buildSelectedPracPhi = (
  sourceList: SkinTypeDefDto['SkinPhi'] | undefined,
  selectedOptionIds: string[]
): AccountSettingsDto['PracPhi'] => {
  const sourceById = new Map<string, AccountSettingsDto['PracPhi'][number]>();

  (sourceList ?? []).forEach((item) => {
    const id = getPhiOptionId(item);
    if (!id) {
      return;
    }

    sourceById.set(id, {
      MVarId: item.MVarId,
      Name: item.Name,
      DName: item.DName,
      UVarTypeId: item.UVarTypeId,
      Pos: item.Pos,
    });
  });

  return selectedOptionIds
    .map((id, index) => {
      const matched = sourceById.get(normalizeOptionId(id));
      if (!matched) {
        return null;
      }

      return {
        ...matched,
        Pos: String(index + 1),
      };
    })
    .filter((item): item is AccountSettingsDto['PracPhi'][number] => !!item);
};

// Registries (touchpoint schedule selections) are keyed off the TouchPointScheduleDto's
// SchedId — same pattern as PracPhi being keyed off SkinPhi's MVarId above — so that a
// selection made against the Touchpoints tab's schedule list round-trips into RegistryId.
const buildSelectedRegistries = (
  sourceList: TouchPointScheduleDto[] | undefined,
  selectedOptionIds: string[]
): RegistyDto[] => {
  const sourceById = new Map<string, TouchPointScheduleDto>();

  (sourceList ?? []).forEach((item) => {
    const id = getScheduleOptionId(item);
    if (!id) {
      return;
    }

    sourceById.set(id, item);
  });

  return selectedOptionIds
    .map((id, index) => {
      const matched = sourceById.get(normalizeOptionId(id));
      if (!matched) {
        return null;
      }

      return {
        RegistryId: matched.SchedId,
        Name: matched.DName,
        Pos: String(index + 1),
      };
    })
    .filter((item): item is RegistyDto => !!item);
};

export const AccountSettingsView: React.FC<AccountSettingsViewProps> = ({
  accountData,
  isLoading = false,
  error = null,
  storage,
  accountId,
  apiService,
  skinDef = null,
  skinDefReady = false,
}) => {
  const enables = accountData?.AccountSettings?.Enables ?? [];
  const pracInfo = accountData?.AccountSettings?.PracInfo;
  const pracPhi = accountData?.AccountSettings?.PracPhi ?? [];
  const skinPhi = skinDef?.SkinPhi ?? [];
  const sraUser = accountData?.SraUser;
  const sraList = accountData?.SraList ?? [];
  const plaList = accountData?.AccountSettings?.PlaList ?? [];
  const registries = accountData?.AccountSettings?.Registries ?? [];
  const assessmentList = skinDef?.AssessmentList ?? [];
  const assessmentOptions = useMemo(() => {
    return assessmentList.map((item) => ({
      id: item.AssessmentId,
      label: item.DName || item.AssessmentId,
      pos: 0,
    }));
  }, [assessmentList]);

  const initialSelectedAssessmentIds = useMemo(() => {
    return plaList
      .filter((pla) => pla.AssessmentId && pla.AssessmentId.trim())
      .sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos))
      .map((pla) => pla.AssessmentId!)
      .filter((id): id is string => !!id);
  }, [plaList]);

  const phiOptions = useMemo(() => {
    return skinPhi
      .map((item) => {
        const id = getPhiOptionId(item);
        if (!id) {
          return null;
        }

        return {
          id,
          label: item.DName || item.Name || id,
        };
      })
      .filter((item): item is OrderedOption => !!item);
  }, [skinPhi]);
  const initialPhiSelectedIds = useMemo(() => {
    const optionSet = new Set(phiOptions.map((option) => normalizeOptionId(option.id)));
    const sorted = [...pracPhi].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));
    const ordered: string[] = [];

    sorted.forEach((item) => {
      const id = getPhiOptionId(item);
      if (!id || !optionSet.has(id) || ordered.includes(id)) {
        return;
      }
      ordered.push(id);
    });

    return ordered;
  }, [phiOptions, pracPhi]);

  const scheduleOptions = useMemo(() => {
    // Same source as the Clients > Touchpoints tab's "Touchpoint Schedules" list
    // (TouchpointsView.tsx) — every schedule on the account, not filtered by Type.
    const schedules = accountData?.Touchpoints?.Schedules ?? [];
    return schedules
      .map((schedule) => {
        const id = getScheduleOptionId(schedule);
        if (!id) {
          return null;
        }

        return { id, label: schedule.DName || id };
      })
      .filter((item): item is OrderedOption => !!item);
  }, [accountData]);

  const initialScheduleSelectedIds = useMemo(() => {
    const optionSet = new Set(scheduleOptions.map((option) => normalizeOptionId(option.id)));

    // Registries saved through this form always round-trip RegistryId as the schedule's own
    // SchedId (see buildSelectedRegistries below) — but the backend appears to reassign its
    // own RegistryId server-side on save (selections survive a save + reload with the right
    // Name, yet RegistryId no longer matches any current SchedId), which left this dual-list
    // permanently unable to preselect anything. Name/DName is the one thing that reliably
    // survives that round-trip, so fall back to matching on it when RegistryId doesn't hit.
    const schedules = accountData?.Touchpoints?.Schedules ?? [];
    const optionIdByName = new Map<string, string>();
    schedules.forEach((schedule) => {
      const optionId = getScheduleOptionId(schedule);
      const nameKey = normalizeOptionId(schedule.DName || '');
      if (optionId && nameKey && !optionIdByName.has(nameKey)) {
        optionIdByName.set(nameKey, optionId);
      }
    });

    const sorted = [...registries].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));
    const ordered: string[] = [];

    sorted.forEach((item) => {
      const registryId = getRegistryOptionId(item);
      const matchedId = registryId && optionSet.has(registryId)
        ? registryId
        : optionIdByName.get(normalizeOptionId(item.Name || ''));

      if (!matchedId || ordered.includes(matchedId)) {
        return;
      }
      ordered.push(matchedId);
    });

    return ordered;
  }, [scheduleOptions, registries, accountData]);

  const accessType = parseInt(sraUser?.AccessType ?? '0', 10);
  const isRestrictedSettingsMode = accessType === 10;
  const isIntermediateSettingsMode = accessType >= 11 && accessType < 30;
  // /account and /system signins — unlocks the extra Account Info rows (Disable account,
  // Is parent, Parent, Skin type) below.
  const isElevatedAccountMode = accessType >= 20 && accessType < 30;
  const hideSettingsSection = false;
  const restrictedSettingKeys = ['enable_idmgmt', 'enable_dobmgmt', 'enable_notifycomplete'];

  const managedEnables = useMemo(
    () => {
      const allEnables = MANAGED_ENABLES.map((entry) => {
        const matched = enables.find((item) => item.Name.trim().toLowerCase() === entry.key);
        return {
          key: entry.key,
          label: entry.label,
          value: matched?.Value ?? '0',
        };
      });

      // Filter based on access mode
      if (isRestrictedSettingsMode) {
        // AccessType = 10: show only the 3 restricted settings
        return allEnables.filter((item) => restrictedSettingKeys.includes(item.key));
      }

      if (isIntermediateSettingsMode) {
        // AccessType 11-29: show all settings including enable_dobmgmt, but exclude enable_notifycomplete
        return allEnables.filter((item) => item.key !== 'enable_notifycomplete');
      }

      // Normal mode (AccessType >= 30): show all settings EXCEPT enable_dobmgmt and enable_notifycomplete
      return allEnables.filter((item) => 
        item.key !== 'enable_dobmgmt' && item.key !== 'enable_notifycomplete'
      );
    },
    [enables, isRestrictedSettingsMode, isIntermediateSettingsMode]
  );
  const initialEnableState = useMemo(
    () => {
      const base = Object.fromEntries(managedEnables.map((item) => [item.key, normalizeEnableValue(item.value)]));
      SCHEDULE_MGMT_SUB_ENABLES.forEach((key) => {
        const matched = enables.find((item) => item.Name.trim().toLowerCase() === key);
        base[key] = normalizeEnableValue(matched?.Value ?? '0');
      });
      return base;
    },
    [managedEnables, enables]
  );
  const initialWebsiteEnableState = useMemo(
    () => Object.fromEntries(plaList.map((entry) => [`${entry.PlaId}:${entry.SCode}`, getWebsiteLinkEnabled(entry)])),
    [plaList]
  );
  const initialSettingEditState = useMemo(
    () => Object.fromEntries(
      managedEnables
        .filter((item) => isSettingAdditionalEditable(item.key))
        .map((item) => [
          item.key,
          {
            alias: item.label,
            notes: '',
            selectedOptionIds: item.key === 'enable_phimgmt'
              ? initialPhiSelectedIds
              : item.key === 'enable_schedulemgmt'
                ? initialScheduleSelectedIds
                : [],
          },
        ])
    ) as Record<string, SettingEditDraft>,
    [initialPhiSelectedIds, initialScheduleSelectedIds, managedEnables]
  );
  const [enableState, setEnableState] = useState<Record<string, string>>(initialEnableState);
  const [websiteEnableState, setWebsiteEnableState] = useState<Record<string, boolean>>(initialWebsiteEnableState);
  const [settingEditState, setSettingEditState] = useState<Record<string, SettingEditDraft>>(initialSettingEditState);
  const [activeSettingEditKey, setActiveSettingEditKey] = useState<string | null>(null);
  const [accountName, setAccountName] = useState(pracInfo?.DName || '');
  const [returnWebsite, setReturnWebsite] = useState(pracInfo?.Website || '');
  const [signInName, setSignInName] = useState(sraUser?.Name || '');
  const [signInEmail, setSignInEmail] = useState(sraUser?.Email || '');
  // Elevated-access-only Account Info fields (PracInfoDto.Enabled/IsParent/Parent/SkinType/AccessType).
  const [disableAccount, setDisableAccount] = useState(!isTruthyValue(pracInfo?.Enabled));
  const [isParentAccount, setIsParentAccount] = useState(isTruthyValue(pracInfo?.IsParent));
  const [parentAccountId, setParentAccountId] = useState(pracInfo?.Parent || '');
  const [selectedSkinType, setSelectedSkinType] = useState(pracInfo?.SkinType || '');
  const [selectedPracAccessType, setSelectedPracAccessType] = useState(pracInfo?.AccessType || '');
  const [parentAccountOptions, setParentAccountOptions] = useState<AccountSearchResult[]>(
    () => getManagedAccountSearchResults().filter((account) => account.isParent)
  );
  const [skinTypeOptions, setSkinTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isSavingRemote, setIsSavingRemote] = useState(false);
  const [hydratedAccountId, setHydratedAccountId] = useState<string | null>(null);
  const [hydratedSettingsSignature, setHydratedSettingsSignature] = useState<string>('none');
  const [isEditingAssessments, setIsEditingAssessments] = useState(false);
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<string[]>(initialSelectedAssessmentIds);
  const hasPendingUserEditsRef = useRef(false);
  const editingAssessmentsRef = useRef(false);

  // ── Pro (accessType 10) assessment settings — see applyProPlaState ──
  const configuredPlas = useMemo(
    () => plaList.filter((pla) => !!pla.AssessmentId?.trim()),
    [plaList]
  );
  const [assessmentsById, setAssessmentsById] = useState<Record<string, AdminAssessmentDto>>({});
  useEffect(() => {
    if (!isRestrictedSettingsMode || !storage || configuredPlas.length === 0) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const assessmentStorage = new AdminAssessmentStorage(storage);
      const loaded: Record<string, AdminAssessmentDto> = {};
      for (const pla of configuredPlas) {
        const assessmentId = pla.AssessmentId!.trim();
        const assessment = await assessmentStorage.getAssessmentBySourceId(assessmentId);
        if (assessment) {
          loaded[assessmentId] = assessment;
        }
      }
      if (!cancelled) {
        setAssessmentsById(loaded);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRestrictedSettingsMode, storage, configuredPlas]);

  const initialProPlaState = useMemo<ProPlaState>(() => {
    const preloadValues: Record<string, Record<string, string>> = {};
    configuredPlas.forEach((pla) => {
      const skinAssessment = assessmentList.find((item) => item.AssessmentId === pla.AssessmentId);
      const values: Record<string, string> = {};
      (skinAssessment?.PreloadOverrides ?? []).forEach((override) => {
        const existing = (pla.Preloads ?? []).find((preload) => preload.FieldCode === override.FieldCode);
        values[override.FieldCode] = existing?.Value ?? override.Value ?? '';
      });
      preloadValues[getPlaKey(pla)] = values;
    });
    return {
      clientReportEnabled: configuredPlas.some((pla) => !!getPlaPreloadValue(pla, ADMIN_REPORT_PRELOAD)),
      preloadValues,
    };
  }, [configuredPlas, assessmentList]);
  const initialProPlaSignature = useMemo(() => JSON.stringify(initialProPlaState), [initialProPlaState]);
  const [proPlaState, setProPlaState] = useState<ProPlaState>(initialProPlaState);
  // What the draft is compared against for dirtiness. Null = the source data's own state; set to
  // the just-saved draft after a save, since the parent's accountData prop may not refresh.
  const [proPlaBaseline, setProPlaBaseline] = useState<ProPlaState | null>(null);
  useEffect(() => {
    if (hasPendingUserEditsRef.current) {
      return;
    }
    setProPlaState(initialProPlaState);
    setProPlaBaseline(null);
    // Keyed on the serialized state so an unchanged-but-recomputed object doesn't wipe edits.
  }, [initialProPlaSignature]);

  // "Enable client report after administration" only means something when at least one
  // configured assessment has a report that could be shown.
  const hasAnyAdminReport = configuredPlas.some(
    (pla) => getAvailableReportIds(pla, assessmentsById[pla.AssessmentId!.trim()]).length > 0
  );
  const proPreloadRows = useMemo(() => {
    const showAssessmentName = configuredPlas.length > 1;
    return configuredPlas.flatMap((pla) => {
      const skinAssessment = assessmentList.find((item) => item.AssessmentId === pla.AssessmentId);
      return (skinAssessment?.PreloadOverrides ?? []).map((override) => ({
        plaKey: getPlaKey(pla),
        fieldCode: override.FieldCode,
        isBool: override.Type === 'bool',
        label: showAssessmentName
          ? `${override.DName || override.FieldCode} (${pla.DName || skinAssessment?.DName || pla.AssessmentId})`
          : (override.DName || override.FieldCode),
      }));
    });
  }, [configuredPlas, assessmentList]);

  const updateProPreloadValue = (plaKey: string, fieldCode: string, value: string) => {
    setProPlaState((current) => ({
      ...current,
      preloadValues: {
        ...current.preloadValues,
        [plaKey]: { ...(current.preloadValues[plaKey] ?? {}), [fieldCode]: value },
      },
    }));
    markUserEdit();
  };
  const currentSettingsSignature = useMemo(() => getAccountSettingsSignature(accountData), [accountData]);
  const resolvedApiService = useMemo(() => resolveApiService(apiService), [apiService]);
  const hasLoadedAccountSettings = !!accountData?.AccountSettings;
  const localStateMatchesCurrentSettings = hydratedSettingsSignature === currentSettingsSignature;

  const markUserEdit = () => {
    hasPendingUserEditsRef.current = true;
    setSaveMessage(null);
  };

  // Pro: the Account Info "Name"/"Email" rows edit the signed-in user's own SRA record. The
  // useState initializers above run before the parent's account data has necessarily arrived
  // (sraUser is undefined on that first render) and nothing else re-applies them — so without
  // this the inputs stayed blank instead of showing the current name/email. Re-sync whenever the
  // record's values change, unless the user has edits in progress.
  useEffect(() => {
    if (hasPendingUserEditsRef.current) {
      return;
    }
    setSignInName(sraUser?.Name || '');
    setSignInEmail(sraUser?.Email || '');
  }, [sraUser?.Name, sraUser?.Email]);

  const updateAssessmentSelection = (selectedIds: string[]) => {
    setSelectedAssessmentIds(selectedIds);
    markUserEdit();
  };

  useEffect(() => {
    console.log('🔍 useEffect[selectedAssessmentIds sync] triggered');
    console.log('  hasLoadedAccountSettings:', hasLoadedAccountSettings);
    console.log('  isEditingAssessments:', isEditingAssessments);
    console.log('  editingAssessmentsRef.current:', editingAssessmentsRef.current);
    console.log('  initialSelectedAssessmentIds:', initialSelectedAssessmentIds);
    console.log('  current selectedAssessmentIds:', selectedAssessmentIds);
    
    if (hasLoadedAccountSettings && !isEditingAssessments && !editingAssessmentsRef.current) {
      console.log('  ⚠️ RESETTING selectedAssessmentIds to initialSelectedAssessmentIds');
      setSelectedAssessmentIds(initialSelectedAssessmentIds);
    } else {
      console.log('  ✅ Skipping reset - editing in progress or not loaded');
    }
  }, [hasLoadedAccountSettings, isEditingAssessments]);

  useEffect(() => {
    // skinDefReady: initialSettingEditState (below) folds in phiOptions/initialPhiSelectedIds,
    // which depend on skinDef.SkinPhi — a value that arrives asynchronously (see
    // useAccountSkinDef) and isn't part of currentSettingsSignature. Without this gate, a run
    // that lands before skinDef resolves would capture an empty-skin selection into
    // settingEditState, and — since nothing about currentSettingsSignature changes once skinDef
    // does — the "sameAccountAlreadyHydrated" check below would skip every later run and that
    // empty selection would never get corrected.
    if (!hasLoadedAccountSettings || !skinDefReady) {
      return;
    }

    const sameAccountAlreadyHydrated = !!(accountId && hydratedAccountId === accountId);
    const hasFreshSettingsForSameAccount = sameAccountAlreadyHydrated && hydratedSettingsSignature !== currentSettingsSignature;

    if (sameAccountAlreadyHydrated && !hasFreshSettingsForSameAccount) {
      return;
    }

    let cancelled = false;
    hasPendingUserEditsRef.current = false;

    const hydrateDraft = async () => {
      const nextEnableState = initialEnableState;
      const nextWebsiteEnableState = initialWebsiteEnableState;
      const nextSettingEditState = initialSettingEditState;
      const nextAccountName = pracInfo?.DName || '';
      const nextReturnWebsite = pracInfo?.Website || '';
      // Elevated-mode Account Info fields — not part of AccountSettingsDraft (no local-draft
      // persistence for these), but they DO need to be (re-)applied here: pracInfo is only
      // available asynchronously (this component can render, and its useState initializers
      // run, before the parent's own account-data fetch/cache-read resolves), so relying on
      // useState's one-time initial value alone leaves them stuck at a pracInfo-undefined
      // default forever once the real data arrives — this effect is what actually re-syncs them.
      const nextDisableAccount = !isTruthyValue(pracInfo?.Enabled);
      const nextIsParentAccount = isTruthyValue(pracInfo?.IsParent);
      const nextParentAccountId = pracInfo?.Parent || '';
      const nextSelectedSkinType = pracInfo?.SkinType || '';
      const nextSelectedPracAccessType = pracInfo?.AccessType || '';

      const applyAccountInfoFields = () => {
        setDisableAccount(nextDisableAccount);
        setIsParentAccount(nextIsParentAccount);
        setParentAccountId(nextParentAccountId);
        setSelectedSkinType(nextSelectedSkinType);
        setSelectedPracAccessType(nextSelectedPracAccessType);
      };

      if (!accountId || !storage?.getItem) {
        if (!cancelled) {
          if (hasPendingUserEditsRef.current) {
            setHydratedAccountId(accountId ?? null);
            return;
          }
          setEnableState(nextEnableState);
          setWebsiteEnableState(nextWebsiteEnableState);
          setSettingEditState(nextSettingEditState);
          setAccountName(nextAccountName);
          setReturnWebsite(nextReturnWebsite);
          applyAccountInfoFields();
          setHydratedAccountId(accountId ?? null);
          setHydratedSettingsSignature(currentSettingsSignature);
        }
        return;
      }

      try {
        const raw = await storage.getItem(getAccountSettingsDraftKey(accountId));
        if (!cancelled && raw) {
          if (hasPendingUserEditsRef.current) {
            setHydratedAccountId(accountId);
            return;
          }
          const parsed = JSON.parse(raw) as Partial<AccountSettingsDraft> | null;
          if (parsed && typeof parsed === 'object') {
            const draftSourceSignature = typeof parsed.sourceSignature === 'string'
              ? parsed.sourceSignature
              : 'none';

            if (draftSourceSignature === currentSettingsSignature) {
              setEnableState({ ...nextEnableState, ...(parsed.enableState ?? {}) });
              setWebsiteEnableState({ ...nextWebsiteEnableState, ...(parsed.websiteEnableState ?? {}) });
              setSettingEditState({ ...nextSettingEditState, ...(parsed.settingEditState ?? {}) });
              setAccountName(typeof parsed.accountName === 'string' ? parsed.accountName : nextAccountName);
              setReturnWebsite(typeof parsed.returnWebsite === 'string' ? parsed.returnWebsite : nextReturnWebsite);
              applyAccountInfoFields();
              setHydratedAccountId(accountId);
              setHydratedSettingsSignature(currentSettingsSignature);
              return;
            }

            setEnableState(nextEnableState);
            setWebsiteEnableState(nextWebsiteEnableState);
            setSettingEditState(nextSettingEditState);
            setAccountName(nextAccountName);
            setReturnWebsite(nextReturnWebsite);
            applyAccountInfoFields();
            setHydratedAccountId(accountId);
            setHydratedSettingsSignature(currentSettingsSignature);
            return;
          }
        }
      } catch (draftError) {
        console.warn('AccountSettingsView: failed to load local draft', draftError);
      }

      if (!cancelled) {
        if (hasPendingUserEditsRef.current) {
          setHydratedAccountId(accountId);
          return;
        }
        setEnableState(nextEnableState);
        setWebsiteEnableState(nextWebsiteEnableState);
        setSettingEditState(nextSettingEditState);
        setAccountName(nextAccountName);
        setReturnWebsite(nextReturnWebsite);
        applyAccountInfoFields();
        setHydratedAccountId(accountId);
        setHydratedSettingsSignature(currentSettingsSignature);
      }
    };

    void hydrateDraft();

    return () => {
      cancelled = true;
    };
  }, [
    accountId,
    hasLoadedAccountSettings,
    currentSettingsSignature,
    hydratedAccountId,
    hydratedSettingsSignature,
    initialEnableState,
    initialSettingEditState,
    initialWebsiteEnableState,
    pracInfo?.DName,
    pracInfo?.Website,
    pracInfo?.Enabled,
    pracInfo?.IsParent,
    pracInfo?.Parent,
    pracInfo?.SkinType,
    pracInfo?.AccessType,
    skinDefReady,
    storage,
  ]);

  useEffect(() => {
    if (!accountId || !storage?.setItem || hydratedAccountId !== accountId || !hasLoadedAccountSettings || !localStateMatchesCurrentSettings) {
      return;
    }

    const payload: AccountSettingsDraft = {
      sourceSignature: currentSettingsSignature,
      enableState,
      websiteEnableState,
      settingEditState,
      accountName,
      returnWebsite,
    };

    void storage.setItem(getAccountSettingsDraftKey(accountId), JSON.stringify(payload));
  }, [accountId, accountName, currentSettingsSignature, enableState, hasLoadedAccountSettings, hydratedAccountId, localStateMatchesCurrentSettings, returnWebsite, settingEditState, storage, websiteEnableState]);

  // "Parent" dropdown — the current Manage Accounts search results (the "account selection
  // list"), filtered to accounts flagged IsParent. Refreshes whenever a new account search
  // updates the shared registry (same event ProGroupsSubmenuBar's own account switcher listens for).
  useEffect(() => {
    if (!isElevatedAccountMode) {
      return;
    }

    const refresh = () => {
      setParentAccountOptions(getManagedAccountSearchResults().filter((account) => account.isParent));
    };

    refresh();

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('progroups:account-selector-options-updated', refresh);
      return () => {
        window.removeEventListener('progroups:account-selector-options-updated', refresh);
      };
    }
  }, [isElevatedAccountMode]);

  // "Skin type" dropdown — every SkinTypeDef synced locally via the bundle-content pipeline.
  useEffect(() => {
    if (!isElevatedAccountMode || !storage) {
      return;
    }

    let cancelled = false;
    void new SkinTypeDefStorage(storage).getAllSkinTypeDefs().then((defs) => {
      if (cancelled) {
        return;
      }
      setSkinTypeOptions(defs.map((def) => ({ value: def.skinType, label: def.name || `Skin ${def.skinType}` })));
    });
    return () => {
      cancelled = true;
    };
  }, [isElevatedAccountMode, storage]);

  const isDirty = useMemo(() => {
    const enableDirty = MANAGED_ENABLES.some((entry) => (enableState[entry.key] ?? '0') !== (initialEnableState[entry.key] ?? '0'))
      || SCHEDULE_MGMT_SUB_ENABLES.some((key) => (enableState[key] ?? '0') !== (initialEnableState[key] ?? '0'));
    const websiteEnableDirty = plaList.some((entry) => {
      const id = `${entry.PlaId}:${entry.SCode}`;
      return !!websiteEnableState[id] !== !!initialWebsiteEnableState[id];
    });
    const settingEditDirty = JSON.stringify(settingEditState) !== JSON.stringify(initialSettingEditState);
    const assessmentsDirty = JSON.stringify(selectedAssessmentIds) !== JSON.stringify(initialSelectedAssessmentIds);
    const sraUserDirty = signInName !== (sraUser?.Name || '') || signInEmail !== (sraUser?.Email || '');
    const accountInfoDirty = isElevatedAccountMode && (
      disableAccount !== !isTruthyValue(pracInfo?.Enabled)
      || isParentAccount !== isTruthyValue(pracInfo?.IsParent)
      || parentAccountId !== (pracInfo?.Parent || '')
      || selectedSkinType !== (pracInfo?.SkinType || '')
      || selectedPracAccessType !== (pracInfo?.AccessType || '')
    );
    const proPlaDirty = isRestrictedSettingsMode
      && JSON.stringify(proPlaState) !== JSON.stringify(proPlaBaseline ?? initialProPlaState);
    return enableDirty
      || websiteEnableDirty
      || settingEditDirty
      || assessmentsDirty
      || proPlaDirty
      || accountName !== (pracInfo?.DName || '')
      || returnWebsite !== (pracInfo?.Website || '')
      || sraUserDirty
      || accountInfoDirty;
  }, [
    accountName,
    disableAccount,
    enableState,
    initialEnableState,
    initialSettingEditState,
    initialWebsiteEnableState,
    initialSelectedAssessmentIds,
    initialProPlaState,
    isElevatedAccountMode,
    isParentAccount,
    isRestrictedSettingsMode,
    parentAccountId,
    plaList,
    proPlaBaseline,
    proPlaState,
    pracInfo?.AccessType,
    pracInfo?.DName,
    pracInfo?.Enabled,
    pracInfo?.IsParent,
    pracInfo?.Parent,
    pracInfo?.SkinType,
    pracInfo?.Website,
    returnWebsite,
    selectedAssessmentIds,
    selectedPracAccessType,
    selectedSkinType,
    settingEditState,
    signInName,
    signInEmail,
    sraUser,
    websiteEnableState,
  ]);

  const showSignInFields = accessType === 10;

  const accountInfoRows = [
    {
      label: 'Account Name',
      value: accountName,
      editable: true,
      onChangeText: setAccountName,
    },
    {
      label: 'Return Website',
      value: returnWebsite,
      editable: true,
      onChangeText: setReturnWebsite,
    },
    ...(showSignInFields ? [
      {
        label: 'Name',
        value: signInName,
        editable: true,
        onChangeText: setSignInName,
      },
      {
        label: 'Email',
        value: signInEmail,
        editable: true,
        onChangeText: setSignInEmail,
      },
    ] : []),
  ];

  const activeSettingEdit = activeSettingEditKey
    ? managedEnables.find((item) => item.key === activeSettingEditKey)
    : null;

  const updateSettingEditField = (settingKey: string, field: keyof SettingEditDraft, value: string) => {
    setSettingEditState((current) => ({
      ...current,
      [settingKey]: {
        alias: current[settingKey]?.alias ?? '',
        notes: current[settingKey]?.notes ?? '',
        selectedOptionIds: current[settingKey]?.selectedOptionIds ?? [],
        [field]: value,
      },
    }));
    markUserEdit();
  };

  const updateSettingSelectedOptions = (settingKey: string, selectedOptionIds: string[]) => {
    setSettingEditState((current) => ({
      ...current,
      [settingKey]: {
        alias: current[settingKey]?.alias ?? '',
        notes: current[settingKey]?.notes ?? '',
        selectedOptionIds,
      },
    }));
    markUserEdit();
  };

  const openLink = async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch (linkError) {
      console.warn('AccountSettingsView: failed to open link', linkError);
    }
  };

  const handleSave = async () => {
    if (isSavingRemote) {
      return;
    }

    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();
    if (!resolvedAccountId) {
      setSaveMessage('Could not save: missing account id.');
      return;
    }

    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setSaveMessage('Could not save: account settings service unavailable.');
      return;
    }

    const existingSettings = accountData?.AccountSettings;
    if (!existingSettings) {
      setSaveMessage('Could not save: account settings are not loaded yet.');
      return;
    }

    const selectedPracPhi = buildSelectedPracPhi(
      skinDef?.SkinPhi,
      settingEditState.enable_phimgmt?.selectedOptionIds ?? initialPhiSelectedIds
    );

    const selectedRegistries = buildSelectedRegistries(
      accountData?.Touchpoints?.Schedules,
      settingEditState.enable_schedulemgmt?.selectedOptionIds ?? initialScheduleSelectedIds
    );

    // "Manage assessments list" (the dual-list UI that lets an admin pick which SkinDef
    // assessments are active) only renders when assessmentList.length > 1 — with exactly one
    // assessment there's nothing to "manage", so it's hidden. But that assessment still needs
    // to end up in PlaList to actually be enabled for the practice, and nothing else ever adds
    // it there without that UI. Auto-include it here (purely additive — never removes an
    // existing selection) so a single-assessment skindef Just Works on Save.
    const resolvedSelectedAssessmentIds = assessmentList.length === 1 && assessmentList[0]?.AssessmentId
      ? Array.from(new Set([...selectedAssessmentIds, assessmentList[0].AssessmentId]))
      : selectedAssessmentIds;

    // A native <select> with no matching option for its bound value visually falls back to
    // showing the first option, but the underlying state (and thus what onChange ever fires
    // with) stays whatever it already was — so if the user never touches the dropdown, the
    // displayed selection and the value we're about to send disagree. Resolve that here so the
    // submitted value always matches what's on screen, rather than sending a blank SkinType.
    const resolvedSkinType = selectedSkinType || skinTypeOptions[0]?.value || '';
    // Same unmatched-<select>-falls-back-to-first-option trap as SkinType above.
    const resolvedPracAccessType = selectedPracAccessType || ACCOUNT_TYPE_OPTIONS[0].value;

    const assembledPlaList = await updatePlaListWithAssessments(
      updatePlaListEnableState(existingSettings.PlaList, websiteEnableState),
      resolvedSelectedAssessmentIds,
      storage
    );
    // Pro (accessType 10): also write the client-report switch and per-assessment preloads
    // edited in Settings onto the PLAs (see applyProPlaState).
    const nextPlaList = isRestrictedSettingsMode
      ? applyProPlaState(
        assembledPlaList,
        proPlaState,
        assessmentList,
        assessmentsById,
        existingSettings.UVars ?? []
      )
      : assembledPlaList;

    const nextAccountSettingsUpdate: AccountSettingsUpdateDto = {
      PracInfo: {
        ...existingSettings.PracInfo,
        DName: accountName.trim(),
        Website: returnWebsite.trim(),
        ...(isElevatedAccountMode ? {
          Enabled: disableAccount ? '0' : '1',
          IsParent: isParentAccount ? '1' : '0',
          Parent: parentAccountId,
          SkinType: resolvedSkinType,
          AccessType: resolvedPracAccessType,
        } : {}),
      },
      Enables: updateEnableSettings(existingSettings.Enables, enableState),
      UVars: existingSettings.UVars ?? [],
      PracPhi: selectedPracPhi,
      Registries: selectedRegistries,
      XInfo: existingSettings.XInfo ?? [],
      PlaList: nextPlaList,
    };

    const payload: PracAccountSettingUpdateDto = {
      AccountId: resolvedAccountId,
      AccountSettingsUpdate: nextAccountSettingsUpdate,
      Status: '1',
    };

    try {
      setIsSavingRemote(true);
      setSaveMessage(null);

      await resolvedApiService.processServiceRequestWithRefresh({
        RequestType: 'provider_accountsetting_put',
        Payload: JSON.stringify(payload),
      }, 'sync', {
        userStore: resolveUserStore(),
        storage,
      });

      // Trust what we just sent, not the backend's echoed-back DTO — provider_accountsetting_put's
      // response doesn't reliably round-trip the full update (previously only PracInfo was
      // known to be affected and special-cased here, but the same unreliable echo was still
      // being trusted for Enables/PracPhi/Registries/PlaList/etc., so an incomplete echo
      // silently corrupted the cache with e.g. an empty Enables array the moment it was next
      // read — surviving even a later sign-out/sign-in, since that reads this same cache rather
      // than re-fetching). nextAccountSettingsUpdate is already the complete, correct next
      // state — it was built directly from existingSettings plus this save's edits — so use
      // it as-is.
      const persistedSettings: AccountSettingsDto = {
        ...existingSettings,
        ...nextAccountSettingsUpdate,
      };

      if (storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          const cached = await pracStorage.getAccountPracInfo(resolvedAccountId);

          if (cached) {
            await pracStorage.saveAccountPracInfo(resolvedAccountId, {
              ...cached,
              AccountSettings: persistedSettings,
            });
          }
        } catch (saveError) {
          console.warn('AccountSettingsView: failed to persist account settings cache after provider_accountsetting_put', saveError);
        }
      }

      // Save SraUser if sign-in fields changed
      const sraUserDirty = signInName !== (sraUser?.Name || '') || signInEmail !== (sraUser?.Email || '');
      if (sraUserDirty && sraUser) {
        try {
          const sraPayload: PracSraUpdateDto = {
            AccountId: resolvedAccountId,
            Sra: {
              ...sraUser,
              Name: signInName.trim(),
              Email: signInEmail.trim(),
            },
            Status: '1',
          };

          const sraResponse = await resolvedApiService.processServiceRequestWithRefresh({
            RequestType: 'provider_sra_put',
            Payload: JSON.stringify(sraPayload),
          }, 'sync', {
            userStore: resolveUserStore(),
            storage,
          });

          let sraResponseDto: PracSraUpdateDto | null = null;
          if (sraResponse?.payload) {
            try {
              sraResponseDto = JSON.parse(sraResponse.payload) as PracSraUpdateDto;
            } catch {
              sraResponseDto = null;
            }
          }

          const updatedSraUser = sraResponseDto?.Sra ?? sraPayload.Sra;

          if (storage) {
            try {
              const pracStorage = new AccountPracInfoStorage(storage);
              const cached = await pracStorage.getAccountPracInfo(resolvedAccountId);

              if (cached) {
                await pracStorage.saveAccountPracInfo(resolvedAccountId, {
                  ...cached,
                  SraUser: updatedSraUser,
                });
              }
            } catch (saveError) {
              console.warn('AccountSettingsView: failed to persist SraUser cache after provider_sra_put', saveError);
            }
          }
        } catch (sraError) {
          console.warn('AccountSettingsView: failed to save SraUser', sraError);
        }
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
          detail: {
            accountId: resolvedAccountId,
          },
        }));
      }

      setSaveMessage('Account settings saved.');
      hasPendingUserEditsRef.current = false;
      setProPlaBaseline(proPlaState);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : String(saveError);
      setSaveMessage(`Save failed: ${message}`);
      console.warn('AccountSettingsView: provider_accountsetting_put failed', saveError);
    } finally {
      setIsSavingRemote(false);
    }
  };

  const renderSettingsContent = () => {
    if (hideSettingsSection) {
      return null;
    }

    if (managedEnables.length === 0) {
      return <Text style={styles.placeholderText}>No account settings enables available yet.</Text>;
    }

    // AccessType >= 20 can hide/unhide a setting row inline (+/− toggle after the label);
    // a setting hidden this way disappears entirely below that access level.
    const canManageVisibility = accessType >= 20;

    const toggleHidden = (key: string) => {
      setEnableState((current) => ({
        ...current,
        [key]: (current[key] ?? '0') === '-1' ? '0' : '-1',
      }));
      markUserEdit();
    };

    return (
      <View style={styles.stackGroup}>
        {managedEnables.map((item) => {
          const state = enableState[item.key] ?? '0';
          const isHidden = state === '-1';

          if (isHidden && !canManageVisibility) {
            // Hidden settings are invisible below the accessType that can manage visibility.
            return null;
          }

          if (isHidden) {
            // Collapsed row: label only (italic) + "+" to restore it.
            return (
              <View key={item.key} style={styles.settingRowWrap}>
                <View style={styles.settingLeftSide}>
                  <Text style={[styles.checkboxLabel, styles.settingLabelHidden]}>{item.label}</Text>
                  <Pressable style={styles.settingVisibilityBtn} onPress={() => toggleHidden(item.key)}>
                    <Text style={styles.settingEditBtnText}>+</Text>
                  </Pressable>
                </View>
              </View>
            );
          }

          const checked = state === '1';
          const canEdit = !isRestrictedSettingsMode && isSettingAdditionalEditable(item.key);

          return (
            <View key={item.key} style={styles.settingRowWrap}>
              <View style={styles.settingLeftSide}>
                {Platform.OS === 'web' ? (
                  renderWebCheckbox(checked, (nextChecked) => {
                    setEnableState((current) => ({
                      ...current,
                      [item.key]: nextChecked ? '1' : '0',
                    }));
                    markUserEdit();
                  }, item.label)
                ) : (
                  <Pressable
                    style={styles.checkboxTouchable}
                    onPress={() => {
                      setEnableState((current) => ({
                        ...current,
                        [item.key]: (current[item.key] ?? '0') === '1' ? '0' : '1',
                      }));
                      markUserEdit();
                    }}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked }}
                  >
                    {renderCheckbox(checked)}
                  </Pressable>
                )}
                <Text style={styles.checkboxLabel}>{item.label}</Text>
                {canManageVisibility ? (
                  <Pressable style={styles.settingVisibilityBtn} onPress={() => toggleHidden(item.key)}>
                    <Text style={styles.settingEditBtnText}>−</Text>
                  </Pressable>
                ) : null}
              </View>

              {canEdit ? (
                <Pressable
                  style={[styles.settingEditBtn, activeSettingEditKey === item.key && styles.settingEditBtnActive]}
                  onPress={() => {
                    setActiveSettingEditKey((current) => (current === item.key ? null : item.key));
                    setIsEditingAssessments(false);
                    editingAssessmentsRef.current = false;
                  }}
                >
                  <Text style={styles.settingEditBtnText}>Edit</Text>
                </Pressable>
              ) : null}
            </View>
          );
        })}

        {/* Pro (accessType 10) — assessment settings that used to live on the Assessment Options
            tab: the client-report switch, then each configured assessment's preloads. */}
        {isRestrictedSettingsMode && configuredPlas.length > 0 && hasAnyAdminReport ? (
          <View style={styles.settingRowWrap}>
            <View style={styles.settingLeftSide}>
              {renderProCheckbox(
                proPlaState.clientReportEnabled,
                (next) => {
                  setProPlaState((current) => ({ ...current, clientReportEnabled: next }));
                  markUserEdit();
                },
                'Enable client report after administration'
              )}
              <Text style={styles.checkboxLabel}>Enable client report after administration</Text>
            </View>
          </View>
        ) : null}

        {isRestrictedSettingsMode ? proPreloadRows.map((row) => {
          const value = proPlaState.preloadValues[row.plaKey]?.[row.fieldCode] ?? '';
          return (
            <View key={`${row.plaKey}:${row.fieldCode}`} style={styles.settingRowWrap}>
              <View style={styles.settingLeftSide}>
                {row.isBool ? (
                  <>
                    {renderProCheckbox(
                      value === '1',
                      (next) => updateProPreloadValue(row.plaKey, row.fieldCode, next ? '1' : '0'),
                      row.label
                    )}
                    <Text style={styles.checkboxLabel}>{row.label}</Text>
                  </>
                ) : (
                  <>
                    <Text style={styles.settingInputLabel}>{row.label}</Text>
                    <TextInput
                      style={styles.settingInput}
                      value={value}
                      onChangeText={(text) => updateProPreloadValue(row.plaKey, row.fieldCode, text)}
                      placeholder={row.label}
                      placeholderTextColor={EDITOR_COLORS.placeholder}
                      autoCapitalize="none"
                    />
                  </>
                )}
              </View>
            </View>
          );
        }) : null}
      </View>
    );
  };

  // Checkbox used by the Pro-mode rows above — same web/native pair the Settings rows use.
  const renderProCheckbox = (checked: boolean, onChange: (next: boolean) => void, label: string) => {
    if (Platform.OS === 'web') {
      return renderWebCheckbox(checked, onChange, label);
    }
    return (
      <Pressable
        style={styles.checkboxTouchable}
        onPress={() => onChange(!checked)}
        accessibilityRole="checkbox"
        accessibilityState={{ checked }}
      >
        {renderCheckbox(checked)}
      </Pressable>
    );
  };

  const renderAccountInfoContent = () => {
    return (
      <View style={styles.accountInfoTable}>
        {accountInfoRows.map((row) => (
          <View key={row.label} style={styles.accountInfoTableRow}>
            <View style={styles.accountInfoLabelCell}>
              <Text style={styles.accountInfoLabel}>{row.label}</Text>
            </View>
            <View style={styles.accountInfoValueCell}>
              {row.editable ? (
                <TextInput
                  style={styles.accountInfoInput}
                  value={row.value}
                  onChangeText={(value) => {
                    row.onChangeText?.(value);
                    markUserEdit();
                  }}
                  autoCapitalize={row.label === 'Return Website' || row.label === 'Email' ? 'none' : 'words'}
                  keyboardType={row.label === 'Email' ? 'email-address' : undefined}
                  placeholder={row.label}
                  placeholderTextColor={EDITOR_COLORS.placeholder}
                />
              ) : (
                <Text style={styles.infoRowValue}>{row.value}</Text>
              )}
            </View>
          </View>
        ))}

        {/* Not for Pro (accessType 10): those accounts keep whatever assessments are configured. */}
        {assessmentList.length > 1 && !isRestrictedSettingsMode ? (
          <View key="assessment-list" style={styles.accountInfoTableRow}>
            <View style={styles.accountInfoLabelCell} />
            <View style={[styles.accountInfoValueCell, styles.inlineLabelWithButton]}>
              <Text style={styles.assessmentListLabel}>Manage assessments list</Text>
              <Pressable
                style={[styles.settingEditBtn, isEditingAssessments && styles.settingEditBtnActive]}
                onPress={() => {
                  const nextState = !isEditingAssessments;
                  editingAssessmentsRef.current = nextState;
                  setIsEditingAssessments(nextState);
                  setActiveSettingEditKey(null);
                }}
              >
                <Text style={styles.settingEditBtnText}>Edit</Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        {isElevatedAccountMode ? (
          <>
            <View style={styles.accountInfoTableRow}>
              <View style={styles.accountInfoLabelCell}>
                <Text style={styles.accountInfoLabel}>Parent</Text>
              </View>
              <View style={styles.accountInfoValueCell}>
                {Platform.OS === 'web' ? (
                  React.createElement(
                    'select' as any,
                    {
                      value: parentAccountId,
                      onChange: (event: any) => {
                        setParentAccountId(String(event?.target?.value ?? ''));
                        markUserEdit();
                      },
                      style: webDropdownStyle,
                      'aria-label': 'Parent account',
                    },
                    [
                      React.createElement('option' as any, { key: '', value: '' }, 'None'),
                      ...parentAccountOptions.map((option) =>
                        React.createElement('option' as any, { key: option.accountId, value: option.accountId }, option.accountName)
                      ),
                    ]
                  )
                ) : (
                  <Text style={styles.infoRowValue}>
                    {parentAccountOptions.find((option) => option.accountId === parentAccountId)?.accountName || 'None'}
                  </Text>
                )}
              </View>
            </View>

            <View style={styles.accountInfoTableRow}>
              <View style={styles.accountInfoLabelCell}>
                <Text style={styles.accountInfoLabel}>Skin type</Text>
              </View>
              <View style={styles.accountInfoValueCell}>
                {Platform.OS === 'web' ? (
                  React.createElement(
                    'select' as any,
                    {
                      value: selectedSkinType,
                      onChange: (event: any) => {
                        setSelectedSkinType(String(event?.target?.value ?? ''));
                        markUserEdit();
                      },
                      style: webDropdownStyle,
                      'aria-label': 'Skin type',
                    },
                    skinTypeOptions.map((option) =>
                      React.createElement('option' as any, { key: option.value, value: option.value }, option.label)
                    )
                  )
                ) : (
                  <Text style={styles.infoRowValue}>
                    {skinTypeOptions.find((option) => option.value === selectedSkinType)?.label || selectedSkinType || 'None'}
                  </Text>
                )}
              </View>
            </View>

            <View style={styles.accountInfoTableRow}>
              <View style={styles.accountInfoLabelCell}>
                <Text style={styles.accountInfoLabel}>Account type</Text>
              </View>
              <View style={styles.accountInfoValueCell}>
                {Platform.OS === 'web' ? (
                  React.createElement(
                    'select' as any,
                    {
                      value: selectedPracAccessType,
                      onChange: (event: any) => {
                        setSelectedPracAccessType(String(event?.target?.value ?? ''));
                        markUserEdit();
                      },
                      style: webDropdownStyle,
                      'aria-label': 'Account type',
                    },
                    ACCOUNT_TYPE_OPTIONS.map((option) =>
                      React.createElement('option' as any, { key: option.value, value: option.value }, option.label)
                    )
                  )
                ) : (
                  <Text style={styles.infoRowValue}>
                    {ACCOUNT_TYPE_OPTIONS.find((option) => option.value === selectedPracAccessType)?.label || 'None'}
                  </Text>
                )}
              </View>
            </View>

            <View style={styles.accountInfoTableRow}>
              <View style={styles.accountInfoLabelCell} />
              <View style={[styles.accountInfoValueCell, styles.inlineCheckboxRow]}>
                <View style={styles.inlineCheckboxItem}>
                  {Platform.OS === 'web' ? (
                    renderWebCheckbox(disableAccount, (next) => {
                      setDisableAccount(next);
                      markUserEdit();
                    }, 'Disable account')
                  ) : (
                    <Pressable
                      style={styles.checkboxTouchable}
                      onPress={() => { setDisableAccount(!disableAccount); markUserEdit(); }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: disableAccount }}
                    >
                      {renderCheckbox(disableAccount)}
                    </Pressable>
                  )}
                  <Text style={styles.checkboxLabel}>Disable account</Text>
                </View>
                <View style={styles.inlineCheckboxItem}>
                  {Platform.OS === 'web' ? (
                    renderWebCheckbox(isParentAccount, (next) => {
                      setIsParentAccount(next);
                      markUserEdit();
                    }, 'Is parent')
                  ) : (
                    <Pressable
                      style={styles.checkboxTouchable}
                      onPress={() => { setIsParentAccount(!isParentAccount); markUserEdit(); }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: isParentAccount }}
                    >
                      {renderCheckbox(isParentAccount)}
                    </Pressable>
                  )}
                  <Text style={styles.checkboxLabel}>Is parent</Text>
                </View>
              </View>
            </View>
          </>
        ) : null}
      </View>
    );
  };

  const renderWebsiteLinksContent = () => {
    if (plaList.length === 0) {
      return <Text style={styles.placeholderText}>No website link entries available yet.</Text>;
    }

    const sortedPlaList = [...plaList].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));

    return (
      <>
        <View style={styles.stackGroup}>
          {sortedPlaList.map((entry) => {
            const key = `${entry.PlaId}:${entry.SCode}`;
            const enabled = !!websiteEnableState[key];
            const href = getWebsiteLinkUrl(entry);
            const launchLabel = getLaunchDisplayName(entry);

            return (
              <View key={`${entry.PlaId}:${entry.SCode}`} style={styles.websiteCard}>
                <View style={styles.websiteToggleRow}>
                  {Platform.OS === 'web' ? (
                    <View style={styles.websiteToggleAreaWeb}>
                      {renderWebCheckbox(enabled, (nextChecked) => {
                        setWebsiteEnableState((current) => ({
                          ...current,
                          [key]: nextChecked,
                        }));
                        markUserEdit();
                      }, `Enable ${launchLabel} website link`)}
                      <Pressable
                        accessibilityRole="button"
                        onPress={() => {
                          setWebsiteEnableState((current) => ({
                            ...current,
                            [key]: !current[key],
                          }));
                          markUserEdit();
                        }}
                        style={styles.websiteEnableLabelTouchable}
                      >
                        <Text style={styles.websiteEnableLabel}>Enable {launchLabel} website link</Text>
                      </Pressable>
                    </View>
                  ) : (
                    <Pressable
                      style={styles.websiteToggleAreaNative}
                      onPress={() => {
                        setWebsiteEnableState((current) => ({
                          ...current,
                          [key]: !current[key],
                        }));
                        markUserEdit();
                      }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: enabled }}
                    >
                      {renderCheckbox(enabled)}
                      <Text style={styles.websiteEnableLabel}>Enable {launchLabel} website link</Text>
                    </Pressable>
                  )}
                </View>
                <View style={styles.websiteUrlRow}>
                  <Pressable onPress={() => void openLink(href)} accessibilityRole="link" style={styles.websiteUrlTouchable}>
                    <Text style={styles.websiteUrlText}>{href}</Text>
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>
      </>
    );
  };

  const saveButtonDisabled = isLoading || isSavingRemote || !isDirty;

  return (
    <View style={styles.panel}>
      <View style={styles.topActionsRow}>
        {activeSettingEdit || isEditingAssessments ? (
          <Pressable
            style={styles.backInlineLink}
            onPress={() => {
              setActiveSettingEditKey(null);
              editingAssessmentsRef.current = false;
              setIsEditingAssessments(false);
            }}
          >
            <Text style={styles.backInlineLinkText}>← Back</Text>
          </Pressable>
        ) : null}
        <Pressable
          style={[styles.minimalSubmitBtn, saveButtonDisabled && styles.minimalSubmitBtnDisabled]}
          onPress={() => { void handleSave(); }}
          disabled={saveButtonDisabled}
        >
          <Text style={styles.minimalSubmitBtnText}>{isSavingRemote ? 'Saving...' : 'Save'}</Text>
        </Pressable>
      </View>

      {isLoading ? (
        <View style={styles.loadingRow}>
          <Text style={styles.loadingText}>Loading account settings...</Text>
        </View>
      ) : null}

      {error ? (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Could not refresh account settings</Text>
          <Text style={styles.errorBody}>{error}</Text>
        </View>
      ) : null}

      {saveMessage ? (
        <View style={styles.saveMessageRow}>
          <Text style={styles.saveMessageText}>{saveMessage}</Text>
        </View>
      ) : null}

      {isEditingAssessments ? (
        <View style={styles.settingEditPanel}>

          <View style={styles.settingEditFormRow}>
            <OrderedDualListSelector
              options={assessmentOptions}
              selectedIds={selectedAssessmentIds}
              onChange={updateAssessmentSelection}
              availableTitle="Available Assessments"
              selectedTitle="Selected Assessments"
            />
          </View>
        </View>
      ) : null}

      {activeSettingEdit && !isEditingAssessments ? (
        <View style={styles.settingEditPanel}>
          <Text style={styles.settingEditTitle}>
            {SETTING_EDIT_PANEL_TITLES[activeSettingEdit.key] ?? activeSettingEdit.label}
          </Text>

          {activeSettingEdit.key === 'enable_phimgmt' ? (
            <View style={styles.settingEditFormRow}>
              <OrderedDualListSelector
                options={phiOptions}
                selectedIds={settingEditState[activeSettingEdit.key]?.selectedOptionIds ?? []}
                onChange={(selectedIds) => updateSettingSelectedOptions(activeSettingEdit.key, selectedIds)}
                availableTitle=""
                selectedTitle=""
              />
            </View>
          ) : activeSettingEdit.key === 'enable_schedulemgmt' ? (
            <>
              <View style={styles.settingEditFormRow}>
                <OrderedDualListSelector
                  options={scheduleOptions}
                  selectedIds={settingEditState[activeSettingEdit.key]?.selectedOptionIds ?? []}
                  onChange={(selectedIds) => updateSettingSelectedOptions(activeSettingEdit.key, selectedIds)}
                  availableTitle=""
                  selectedTitle=""
                />
              </View>

              <View style={styles.settingEditFormRow}>
                <View style={styles.inlineCheckboxItem}>
                  {Platform.OS === 'web' ? (
                    renderWebCheckbox((enableState.enable_manualscheduling ?? '0') === '1', (next) => {
                      setEnableState((current) => ({ ...current, enable_manualscheduling: next ? '1' : '0' }));
                      markUserEdit();
                    }, 'Enable manual scheduling')
                  ) : (
                    <Pressable
                      style={styles.checkboxTouchable}
                      onPress={() => {
                        setEnableState((current) => ({
                          ...current,
                          enable_manualscheduling: (current.enable_manualscheduling ?? '0') === '1' ? '0' : '1',
                        }));
                        markUserEdit();
                      }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: (enableState.enable_manualscheduling ?? '0') === '1' }}
                    >
                      {renderCheckbox((enableState.enable_manualscheduling ?? '0') === '1')}
                    </Pressable>
                  )}
                  <Text style={styles.checkboxLabel}>Enable manual scheduling</Text>
                </View>
              </View>

              <View style={styles.settingEditFormRow}>
                <View style={styles.inlineCheckboxItem}>
                  {Platform.OS === 'web' ? (
                    renderWebCheckbox((enableState.enable_touchpointscheduling ?? '0') === '1', (next) => {
                      setEnableState((current) => ({ ...current, enable_touchpointscheduling: next ? '1' : '0' }));
                      markUserEdit();
                    }, 'Enable touchpoint scheduling')
                  ) : (
                    <Pressable
                      style={styles.checkboxTouchable}
                      onPress={() => {
                        setEnableState((current) => ({
                          ...current,
                          enable_touchpointscheduling: (current.enable_touchpointscheduling ?? '0') === '1' ? '0' : '1',
                        }));
                        markUserEdit();
                      }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: (enableState.enable_touchpointscheduling ?? '0') === '1' }}
                    >
                      {renderCheckbox((enableState.enable_touchpointscheduling ?? '0') === '1')}
                    </Pressable>
                  )}
                  <Text style={styles.checkboxLabel}>Enable touchpoint scheduling</Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View style={styles.settingEditFormRow}>
                <Text style={styles.settingEditFieldLabel}>Alias</Text>
                <TextInput
                  style={styles.settingEditInput}
                  value={settingEditState[activeSettingEdit.key]?.alias ?? ''}
                  onChangeText={(value) => updateSettingEditField(activeSettingEdit.key, 'alias', value)}
                  placeholder="Setting alias"
                  placeholderTextColor={EDITOR_COLORS.placeholder}
                />
              </View>

              <View style={styles.settingEditFormRow}>
                <Text style={styles.settingEditFieldLabel}>Notes</Text>
                <TextInput
                  style={[styles.settingEditInput, styles.settingEditNotesInput]}
                  value={settingEditState[activeSettingEdit.key]?.notes ?? ''}
                  onChangeText={(value) => updateSettingEditField(activeSettingEdit.key, 'notes', value)}
                  placeholder="Additional details for this setting"
                  placeholderTextColor={EDITOR_COLORS.placeholder}
                  multiline
                />
              </View>
            </>
          )}

        </View>
      ) : null}

      <View style={styles.editPanel}>
        <View style={styles.editTable}>
          {SECTION_LABELS.filter((section) => {
            // Hide Settings section when AccessType=10 and multiple SraList items
            if (section === 'Settings' && hideSettingsSection) {
              return false;
            }
            return true;
          }).map((section, index, filteredSections) => (
            <React.Fragment key={section}>
              <View style={styles.editTableRow}>
                <View style={styles.editTableLabelCell}>
                  <Text style={styles.editTableLabel}>{section}</Text>
                </View>
                <View style={styles.editTableContentCell}>
                  {section === 'Settings'
                    ? renderSettingsContent()
                    : section === 'Account Info'
                      ? renderAccountInfoContent()
                      : renderWebsiteLinksContent()}
                </View>
              </View>

              {index < filteredSections.length - 1 ? (
                <View style={styles.editTableSpacerRow}>
                  <View style={styles.editTableSpacerLabelCell} />
                  <View style={styles.editTableSpacerContentCell} />
                </View>
              ) : null}
            </React.Fragment>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: ACCOUNT_SETTINGS_SPACING.topPanelPadding,
    backgroundColor: EDITOR_COLORS.background,
  },
  topActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: ACCOUNT_SETTINGS_SPACING.topActionsBottom,
  },
  backInlineLink: {
    marginRight: 10,
  },
  backInlineLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'none',
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorBody: {
    ...editorText,
    lineHeight: 20,
    color: EDITOR_COLORS.errorText,
  },
  loadingRow: {
    paddingBottom: 12,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  saveMessageRow: {
    paddingBottom: 10,
  },
  saveMessageText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
  },
  settingEditPanel: {
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    padding: 10,
    marginBottom: ACCOUNT_SETTINGS_SPACING.editFormToSettingsGap,
  },
  settingEditTitle: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    marginBottom: 6,
  },
  settingEditFormRow: {
    marginBottom: 8,
  },
  settingEditFieldLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginBottom: 4,
  },
  settingEditInput: {
    ...editorText,
    minHeight: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    paddingVertical: 4,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  settingEditNotesInput: {
    minHeight: 56,
    textAlignVertical: 'top',
  },
  editPanel: {
    paddingTop: 4,
    paddingHorizontal: 6,
    paddingBottom: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  editTable: {
    width: '100%',
    borderRadius: 0,
    overflow: 'visible',
  },
  editTableRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: 30,
  },
  editTableSpacerRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: ACCOUNT_SETTINGS_SPACING.sectionSpacerRow,
  },
  editTableSpacerLabelCell: {
    width: 110,
  },
  editTableSpacerContentCell: {
    flex: 1,
  },
  editTableLabelCell: {
    width: 110,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingLeft: 8,
    paddingRight: 18,
    paddingTop: 10,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  editTableLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  editTableContentCell: {
    flex: 1,
    paddingLeft: 18,
    paddingRight: 12,
    paddingTop: ACCOUNT_SETTINGS_SPACING.secondColumnContentTop,
    paddingBottom: ACCOUNT_SETTINGS_SPACING.secondColumnContentBottom,
    justifyContent: 'flex-start',
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    lineHeight: 20,
  },
  stackGroup: {
    gap: ACCOUNT_SETTINGS_SPACING.secondColumnRowGap,
  },
  settingRowWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  settingLeftSide: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingEditBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingEditBtn2: {
    width: 120,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingEditBtnActive: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  settingEditBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  settingVisibilityBtn: {
    width: 25,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  settingLabelHidden: {
    fontStyle: 'italic',
  },
  checkboxTouchable: {
    marginRight: 10,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 2,
  },
  websiteToggleRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  websiteToggleAreaWeb: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  websiteToggleAreaNative: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxBtn: {
    width: 22,
    height: 22,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.accent,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
    marginTop: 0,
  },
  checkboxBtnChecked: {
    backgroundColor: EDITOR_COLORS.accent,
    borderColor: EDITOR_COLORS.accent,
  },
  checkboxMark: {
    fontFamily: 'Arial',
    fontSize: 16,
    color: EDITOR_COLORS.background,
    lineHeight: 16,
    fontWeight: '800',
  },
  checkboxTextBlock: {
    flex: 1,
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    paddingTop: 1,
    marginLeft: 10,
  },
  // Pro-mode text preload rows: label then a compact input on the same line.
  settingInputLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    marginRight: 10,
  },
  settingInput: {
    ...editorText,
    flex: 1,
    maxWidth: 260,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  assessmentListLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    paddingTop: 1,
  },
  websiteEnableLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    marginLeft: 10,
    lineHeight: 20,
  },
  websiteEnableLabelTouchable: {
    justifyContent: 'center',
  },
  checkboxValue: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    marginTop: 2,
  },
  accountInfoTable: {
    width: '100%',
    borderRadius: 0,
    overflow: 'visible',
    backgroundColor: EDITOR_COLORS.background,
  },
  accountInfoTableRow: {
    flexDirection: 'row',
    minHeight: ACCOUNT_SETTINGS_SPACING.accountInfoRowMinHeight,
  },
  accountInfoLabelCell: {
    width: 100,
    paddingLeft: 8,
    paddingRight: 0,
    paddingVertical: ACCOUNT_SETTINGS_SPACING.accountInfoRowVerticalPadding,
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  accountInfoLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  accountInfoValueCell: {
    flex: 1,
    alignItems: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: ACCOUNT_SETTINGS_SPACING.accountInfoRowVerticalPadding,
    justifyContent: 'flex-start',
  },
  inlineCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  inlineCheckboxItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inlineLabelWithButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  accountInfoInput: {
    ...editorText,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  infoRowValue: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  linkValue: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: Platform.OS === 'web' ? 'underline' : 'none',
  },
  websiteCard: {
    borderRadius: 0,
    paddingVertical: ACCOUNT_SETTINGS_SPACING.websiteCardVerticalPadding,
    backgroundColor: EDITOR_COLORS.background,
  },
  websiteUrlRow: {
    width: '100%',
    paddingLeft: 32,
    paddingTop: ACCOUNT_SETTINGS_SPACING.websiteUrlTopPadding,
  },
  websiteUrlTouchable: {
    alignSelf: 'flex-start',
  },
  websiteUrlText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'underline',
    textAlign: 'left',
  },
  minimalSubmitBtn: {
    marginTop: ACCOUNT_SETTINGS_SPACING.saveButtonTop,
    marginLeft: 'auto',
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimalSubmitBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  minimalSubmitBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  editAssessmentsButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    paddingRight: 8,
  },
});
